import type HighchartsType from 'highcharts/highstock';

import { GX_NEUTRAL } from '@/config/colors';

import type { FullscreenRuntimeChart } from './fullScreenChartTypes';

const AXIS_LABEL_FONT_SIZE = '10px';
const AXIS_LABEL_X = 12;
const AXIS_LABEL_Y = 3;
const Y_AXIS_LABEL_OFFSET = 0;
const Y_AXIS_MAX_PADDING = 0.1;

export const INDICATOR_PANE_SEPARATOR_COLOR = GX_NEUTRAL;
export const INDICATOR_PANE_SEPARATOR_WIDTH = 6;

const formatFullscreenYAxisLabel = function (this: { value: unknown }): string {
  const value = Number(this.value);
  return Number.isFinite(value)
    ? Number.parseFloat(value.toFixed(2)).toString()
    : String(this.value);
};

const createFullScreenYAxisLabelsOptions = (): HighchartsType.YAxisOptions['labels'] => ({
  align: 'left',
  formatter: formatFullscreenYAxisLabel,
  x: AXIS_LABEL_X,
  y: AXIS_LABEL_Y,
  style: { fontSize: AXIS_LABEL_FONT_SIZE }
});

const createFullScreenYAxisResizeOptions = (
  resize: HighchartsType.YAxisOptions['resize'] = {}
): HighchartsType.YAxisOptions['resize'] => ({
  ...resize,
  enabled: resize.enabled ?? true,
  lineColor: INDICATOR_PANE_SEPARATOR_COLOR,
  lineWidth: INDICATOR_PANE_SEPARATOR_WIDTH
});

export const createFullScreenYAxisOptions = (
  axisOptions: HighchartsType.YAxisOptions = {}
): HighchartsType.YAxisOptions => ({
  labels: createFullScreenYAxisLabelsOptions(),
  maxPadding: Y_AXIS_MAX_PADDING,
  offset: Y_AXIS_LABEL_OFFSET,
  opposite: true,
  resize: createFullScreenYAxisResizeOptions(axisOptions.resize),
  title: axisOptions.title ?? { text: undefined }
});

const isInternalYAxis = (axis: HighchartsType.Axis): boolean => {
  const axisOptions: HighchartsType.AxisOptions & { isInternal?: boolean } = axis.options;
  return axisOptions.isInternal === true;
};

const needsFullScreenYAxisOptions = (axisOptions: HighchartsType.YAxisOptions): boolean => {
  const labels = axisOptions.labels ?? {};
  const resize = axisOptions.resize ?? {};

  return (
    axisOptions.maxPadding !== Y_AXIS_MAX_PADDING ||
    axisOptions.offset !== Y_AXIS_LABEL_OFFSET ||
    axisOptions.opposite !== true ||
    labels.align !== 'left' ||
    labels.formatter !== formatFullscreenYAxisLabel ||
    labels.style?.fontSize !== AXIS_LABEL_FONT_SIZE ||
    labels.x !== AXIS_LABEL_X ||
    labels.y !== AXIS_LABEL_Y ||
    resize.lineColor !== INDICATOR_PANE_SEPARATOR_COLOR ||
    resize.lineWidth !== INDICATOR_PANE_SEPARATOR_WIDTH
  );
};

export const applyFullscreenYAxisLayout = (
  chart: HighchartsType.Chart,
  shouldRedraw = false
): void => {
  let hasUpdatedAxis = false;

  for (const axis of chart.yAxis) {
    const axisOptions = axis.options as HighchartsType.YAxisOptions;
    if (isInternalYAxis(axis) || !needsFullScreenYAxisOptions(axisOptions)) continue;
    axis.update(createFullScreenYAxisOptions(axisOptions), false);
    hasUpdatedAxis = true;
  }

  if (hasUpdatedAxis && shouldRedraw) {
    chart.redraw(false);
  }
};

export const installFullscreenYAxisSync = (chart: HighchartsType.Chart): void => {
  const navigationBindings = (chart as FullscreenRuntimeChart).navigationBindings;

  if (!navigationBindings?.resizeYAxes || navigationBindings.fullscreenYAxisSyncInstalled) return;

  const resizeYAxes = navigationBindings.resizeYAxes.bind(navigationBindings);
  navigationBindings.resizeYAxes = removedYAxisProps => {
    resizeYAxes(removedYAxisProps);
    applyFullscreenYAxisLayout(chart);
  };
  navigationBindings.fullscreenYAxisSyncInstalled = true;
};
